import httpClient from './auth-http-client'
import { desktopAppVersion, desktopPlatform } from '@/desktop/bridge'
import { loadNativeAppInfo } from '@/native/platformHeaders'
import { getNativePlatform, isNativePlatform } from '@/utils/platform'

/** Session flag so we only offer the browser timezone once per tab. */
const TIMEZONE_SEED_KEY = 'lh_timezone_seed_offered'

function browserTimezone(): string | null {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        return tz && tz !== 'UTC' ? tz : null
    }
    catch {
        return null
    }
}

async function sendUserHeartbeat() {
    try {
        const build = __LIGHTHOUSE_BUILD__
        const payload: {
            client_build_sha: string
            client_build_at: string
            timezone?: string
            client_platform?: string
            client_app_version?: string
        } = {
            client_build_sha: build.sha,
            client_build_at: build.builtAt,
        }
        // Desktop app: which build of the shell this tab runs in (the web build is above).
        const desktopOs = desktopPlatform()
        if (desktopOs) {
            payload.client_platform = `desktop-${desktopOs}`
            payload.client_app_version = desktopAppVersion() ?? undefined
        }
        // Store builds: which binary (the backend records last_client_platform / last_client_app_version).
        if (isNativePlatform()) {
            payload.client_platform = getNativePlatform()
            payload.client_app_version = (await loadNativeAppInfo())?.version || undefined
        }
        // Offer the browser zone until the server has had a chance to seed
        // brief_preferences.timezone (or confirm it is already set). Without
        // this, check-ins confirm times in UTC for anyone who never opened
        // Brief settings.
        if (!sessionStorage.getItem(TIMEZONE_SEED_KEY)) {
            const tz = browserTimezone()
            if (tz) payload.timezone = tz
        }
        const response = await httpClient.post('/user/heartbeat', payload)
        if (response.status === 200 && payload.timezone) {
            sessionStorage.setItem(TIMEZONE_SEED_KEY, '1')
        }
        return response.status === 200
    }
    catch (error) {
        console.error('Error:', error)
        return false
    }
}

async function getUser() {
    try {
        const response = await httpClient.get(`/user`)
        if (response.status === 200) {
            return response.data
        }
        return {}
    }
    catch (error) {
        console.error('Error:', error)
    }
}

async function getUserStats(startDate: string, creatorId?: any) {
    try {
        let path = `/user/stats?start=${startDate}`
        if (creatorId) {
            path = `/user/stats?start=${startDate}&creator_id=${creatorId}`   
        }
        const response = await httpClient.get(path)
        if (response.status === 200) {
            return response.data
        }
        return {}
    }
    catch (error) {
        console.error('Error:', error)
    }
}

async function useUpdateUser(changes: any) {
    try {
        const response = await httpClient.post(`/user/update`, changes)
        if (response.status === 200) {
            return response.data
        }
        return {}
    }
    catch (error) {
        console.error('Error:', error)
    }
}

async function setUserOnboarded() {
    try {
        const response = await httpClient.post(`/user/onboarded`)
        if (response.status === 200) {
            return response.data
        }
        return { error: 'Failed to mark onboarded', status: response.status }
    }
    catch (error: any) {
        console.error('Error:', error)
        const status = error?.response?.status
        const data = error?.response?.data
        return {
            error: data?.error || 'Failed to mark onboarded',
            status,
            ...((data && typeof data === 'object') ? data : {}),
        }
    }
}

async function setUserAvatar(avatar: string) {
    try {
        const response = await httpClient.post(`/user/avatar`, { url: avatar })
        if (response.status === 200) {
            return response.data
        }
        return {}
    }
    catch (error) {
        console.error('Error:', error)
    }
}

/**
 * Merge a partial UI-preferences delta onto the user's `ui_preferences`
 * jsonb column. The server deep-merges so concurrent toggles in
 * different tabs don't clobber each other. Returns the merged blob.
 *
 * Convention: namespace keys by feature, e.g.
 *   { conversations: { hideDismissConfirm: true } }
 */
async function getProductUpdatesPreference() {
    try {
        const response = await httpClient.get('/user/product-updates')
        if (response.status === 200) {
            return response.data as { subscribed: boolean; unsubscribe_url: string }
        }
        return null
    }
    catch (error) {
        console.error('Error:', error)
        return null
    }
}

async function updateProductUpdatesPreference(subscribed: boolean) {
    try {
        const response = await httpClient.patch('/user/product-updates', { subscribed })
        if (response.status === 200) {
            return response.data as { subscribed: boolean; unsubscribe_url: string }
        }
        return null
    }
    catch (error) {
        console.error('Error:', error)
        return null
    }
}

async function updateUiPreferences(delta: Record<string, any>) {
    try {
        const response = await httpClient.patch(`/user/ui-preferences`, delta)
        if (response.status === 200) {
            return response.data as { ui_preferences: Record<string, any> }
        }
        // Return null on failure so callers don't treat `{}` as a successful
        // empty preferences blob and wipe their local user state.
        return null
    }
    catch (error) {
        console.error('Error:', error)
        return null
    }
}

async function setUserProfile(updatedFields: any) {
    try {
        const response = await httpClient.patch(`/user/profile`, updatedFields)
        if (response.status === 200) {
            return response.data
        }
        return {}
    }
    catch (error) {
        console.error('Error:', error)
    }
}

/** Choose the voice the assistant speaks in on calls (saved on the account as the user's own pick). */
async function setAssistantVoice(voice: string): Promise<{ assistant_voice: string } | null> {
    try {
        const response = await httpClient.post('/user/assistant-voice', { voice })
        return response.status === 200 && response.data?.assistant_voice ? response.data : null
    }
    catch (error) {
        console.error('Error:', error)
        return null
    }
}

export {
    getProductUpdatesPreference,
    setAssistantVoice,
    getUserStats,
    getUser,
    sendUserHeartbeat,
    setUserAvatar,
    setUserOnboarded,
    setUserProfile,
    updateProductUpdatesPreference,
    updateUiPreferences,
    useUpdateUser,
}
